import React, { useMemo, useState } from 'react';
import { TemplateModal, Icon, Button, Card } from '@nearst/ui';
import Uppy from '@uppy/core';
import { DragDrop } from '@uppy/react';
import { useShop } from '@services/ShopProvider';
import { uploadProductFile } from '@services/inventory';

import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import styles from './ProductUploadModal.module.scss';
import { useIsChain } from '@utils/useIsChain';

const ValidatingUpload = ({ filename }) => {
	return (
		<>
			<Card className={styles.processingCard} sectioned>
				<p>
					<Icon>description</Icon>
					{filename}
				</p>
			</Card>
			<div className={styles.processingInfo}>Uploading and validating your file...</div>
		</>
	);
};

const SuccessfulUpload = () => {
	return (
		<div>
			<div className={`${styles.processResult} ${styles.successful}`}>
				<h3>
					<Icon>check</Icon> File uploaded
				</h3>
				<div className={styles.processDetailsContainer}>
					<p>We will send you an email once your products have been processed.</p>
				</div>
			</div>
		</div>
	);
};

const UnsuccessfulUpload = ({ error }) => {
	return (
		<div>
			<div className={`${styles.processResult} ${styles.unsuccessful}`}>
				<h3>
					<Icon>clear</Icon> Unable to process file
				</h3>
				<div className={styles.processDetailsContainer}>
					{error === 'File upload failed' ? (
						<>
							<p>File upload failed</p>
							<p>The file failed to upload. Please try again.</p>
						</>
					) : null}
					{error === 'File validation failed' ? (
						<>
							<p>Missing barcode header </p>
							<p>Please make sure your file contains a barcode header and try again.</p>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
};

const ProductUploadModal = ({ close, open }) => {
	const { shop, organisation } = useShop();
	const isChain = useIsChain();
	const [processStatus, setProcessStatus] = useState('');
	const [error, setError] = useState('');
	const [filename, setFilename] = useState('');

	const handleClose = () => {
		if (processStatus === 'validating') return;
		close();
		setProcessStatus('');
		setFilename('');
		setError('');
	};

	const uppy = useMemo(() => {
		const uppy = new Uppy({
			restrictions: {
				maxNumberOfFiles: 1,
				allowedFileTypes: ['text/csv', '.csv', '.txt', '.tsv']
			},
			autoProceed: true,
			allowMultipleUploadBatches: false
		});

		uppy.on('file-added', async (file) => {
			setFilename(file.name);
			try {
				setProcessStatus('validating');
				await uploadProductFile(file.data, organisation.id, isChain ? null : shop.id);
				setProcessStatus('successful');
			} catch (e) {
				console.error(e);
				setError(e.message);
				setProcessStatus('unsuccessful');
			}
		});

		return uppy;
	}, [isChain, organisation.id, shop.id]);

	const templateCsv = `barcode,title,description,brand,image_url,link,menu_categories,tax_percentage,price_markup_deliveroo,price_markup_uber_eats,price_markup_just_eat,classifications,unit_pricing_measure,unit_pricing_base_measure
123456,Product Name,Product Description,Brand Name,https://example.com/image.jpg,https://example.com/product,"Category 1, Category 2",20,15,15,15,"pharmaceuticals_aspirin, pharmaceuticals_paracetamol",500g,1kg`;
	const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8;' });
	const templateUrl = URL.createObjectURL(blob);

	return (
		<TemplateModal isOpen={open} onDismiss={handleClose} title={'Upload product data'} showDismissButton={false}>
			<p>
				You can add product data for multiple barcodes at once by uploading a .csv file containing the barcodes, along with any
				extra product information.
			</p>
			<p>
				The example file below contains all the fields it is possible to add, but your file must have a <strong>barcode</strong>{' '}
				field.
			</p>

			{isChain && !processStatus && (
				<p className={styles.warning}>Uploading this file will override any existing product data for all of your shops.</p>
			)}

			{!processStatus && (
				<>
					<Button href={templateUrl} download="template.csv" link>
						Download example file <Icon>download</Icon>
					</Button>
					<DragDrop className={styles.dragDrop} uppy={uppy} />
				</>
			)}
			{processStatus === 'validating' && <ValidatingUpload filename={filename} />}
			{processStatus === 'successful' && <SuccessfulUpload />}
			{processStatus === 'unsuccessful' && <UnsuccessfulUpload error={error} />}
		</TemplateModal>
	);
};

export default ProductUploadModal;
